import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { subDays, previousFriday } from 'date-fns';
import { toLocalDateStr } from '../lib/utils';
import { getStoredGitAliases, resolveGitAuthor } from './SettingsView';
import { PresetGrid } from './PresetGrid';
import * as api from '@/services/api';
import * as presetStore from '@/services/preset-store';
import { generateAndDownloadReport } from '@/services/api';
import type { Preset, AnalysisJob, HydratedSnapshot, SnapshotData } from '@/services/preset-types';
import { resolveTimeRange, ENGINE_VERSION } from '@/services/preset-types';
import { notifyAnalysisComplete } from '@/services/notifications';
import { NotificationPermissionDialog } from './NotificationPermissionDialog';
import * as historyStore from '@/services/analysis-history-store';
import * as audit from '@/services/audit';
import { usePresetShortcuts } from '@/hooks/usePresetShortcuts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowTrendUp,
  faArrowsUpDown,
  faCalendarDays,
  faChartBar,
  faChevronDown,
  faChevronRight,
  faChevronUp,
  faCircleCheck,
  faCircleXmark,
  faCode,
  faCodeBranch,
  faCodeCommit,
  faCompress,
  faDownload,
  faExpand,
  faFile,
  faFilter,
  faLayerGroup,
  faMagnifyingGlass,
  faMoon,
  faRotate,
  faSpinner,
  faSquare,
  faSquareCheck,
  faSun,
  faUsers,
  faXmark
} from '@fortawesome/free-solid-svg-icons';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, PieChart, Pie, Cell
} from 'recharts';

interface RepoConfig {
  type: 'local' | 'bitbucket' | 'github' | 'gitlab';
  path?: string;
  url?: string;
  accessToken?: string;
  username?: string;
  appPassword?: string;
  name?: string;
  repoId?: string;
}

interface AvailableRepo {
  id: string;
  name: string;
  type: 'local' | 'bitbucket' | 'github' | 'gitlab';
  url?: string;
  path?: string;
  icon?: string;
  description?: string;
  project?: string;
  updatedOn?: string;
}

interface CrossProjectCommit {
  hash: string;
  author: string;
  email: string;
  date: string;
  message: string;
  branch: string;
  additions: number;
  deletions: number;
  isMerge?: boolean;
  repoName: string;
  repoId: string;
}

interface RepoResult {
  repoId: string;
  repoName: string;
  repoType: string;
  stats: {
    totalCommits: number;
    totalAuthors: number;
    totalAdditions: number;
    totalDeletions: number;
    authors: { name: string; commits: number; additions: number; deletions: number; activeDays: number }[];
  } | null;
  commits: CrossProjectCommit[];
  error?: string;
}

interface AggregateStats {
  totalCommits: number;
  totalAuthors: number;
  totalAdditions: number;
  totalDeletions: number;
  totalRepos: number;
}

export interface CrossProjectReviewData {
  results: RepoResult[];
  allCommits: CrossProjectCommit[];
  aggregateStats: AggregateStats | null;
  selectedRepoNames: string[];
  customFrom: string;
  customTo: string;
}

interface CrossProjectViewProps {
  availableRepos: AvailableRepo[];
  onClose: () => void;
  storedUsername?: string | null;
  storedEmail?: string | null;
  storedToken?: string | null;
  githubUsername?: string | null;
  githubToken?: string | null;
  inline?: boolean;
  reviewData?: CrossProjectReviewData;
}

const PROJECT_COLORS = [
  { bg: 'bg-blue-500', text: 'text-blue-400', border: 'border-blue-500/30', light: 'bg-blue-500/10', dot: 'bg-blue-500' },
  { bg: 'bg-emerald-500', text: 'text-emerald-400', border: 'border-emerald-500/30', light: 'bg-emerald-500/10', dot: 'bg-emerald-500' },
  { bg: 'bg-amber-500', text: 'text-amber-400', border: 'border-amber-500/30', light: 'bg-amber-500/10', dot: 'bg-amber-500' },
  { bg: 'bg-purple-500', text: 'text-purple-400', border: 'border-purple-500/30', light: 'bg-purple-500/10', dot: 'bg-purple-500' },
  { bg: 'bg-rose-500', text: 'text-rose-400', border: 'border-rose-500/30', light: 'bg-rose-500/10', dot: 'bg-rose-500' },
  { bg: 'bg-cyan-500', text: 'text-cyan-400', border: 'border-cyan-500/30', light: 'bg-cyan-500/10', dot: 'bg-cyan-500' },
  { bg: 'bg-orange-500', text: 'text-orange-400', border: 'border-orange-500/30', light: 'bg-orange-500/10', dot: 'bg-orange-500' },
  { bg: 'bg-pink-500', text: 'text-pink-400', border: 'border-pink-500/30', light: 'bg-pink-500/10', dot: 'bg-pink-500' },
  { bg: 'bg-indigo-500', text: 'text-indigo-400', border: 'border-indigo-500/30', light: 'bg-indigo-500/10', dot: 'bg-indigo-500' },
  { bg: 'bg-teal-500', text: 'text-teal-400', border: 'border-teal-500/30', light: 'bg-teal-500/10', dot: 'bg-teal-500' },
];

export function CrossProjectView({
  availableRepos,
  onClose,
  storedUsername,
  storedEmail: _storedEmail,
  storedToken,
  githubUsername: _githubUsername,
  githubToken: _githubToken,
  inline,
  reviewData,
}: CrossProjectViewProps) {
  const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains('dark'));
  const [currentTime, setCurrentTime] = useState(new Date());
  const [selectedRepoIds, setSelectedRepoIds] = useState<Set<string>>(new Set());
  const [results, setResults] = useState<RepoResult[]>([]);
  const [allCommits, setAllCommits] = useState<CrossProjectCommit[]>([]);
  const [aggregateStats, setAggregateStats] = useState<AggregateStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [progressMessage, setProgressMessage] = useState<string>('');
  const [progressIndex, setProgressIndex] = useState(0);
  const [progressTotal, setProgressTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [showSelector, setShowSelector] = useState(true);
  const [hideMerges, setHideMerges] = useState(false);
  const [authorFilter, setAuthorFilter] = useState<Set<string>>(new Set());
  const [repoFilter, setRepoFilter] = useState<Set<string>>(new Set());
  const [branchFilter, setBranchFilter] = useState<string>('all');
  const [messageSearch, setMessageSearch] = useState('');
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [showFilters, setShowFilters] = useState(true);
  const [repoSearch, setRepoSearch] = useState('');
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(false);
  const [visibleCount, setVisibleCount] = useState(50);
  const [showCharts, setShowCharts] = useState(true);
  const [expandTimeline, setExpandTimeline] = useState(false);
  const [dateRangePreset, setDateRangePreset] = useState<number | 'friday-monday'>(28); // days
  const [customFrom, setCustomFrom] = useState<string>(() => {
    const d = new Date(); d.setDate(d.getDate() - 28); return toLocalDateStr(d);
  });
  const [customTo, setCustomTo] = useState<string>(toLocalDateStr());
  const [showDatePicker, setShowDatePicker] = useState(false);

  const [isReview, setIsReview] = useState(false);

  // Diff viewer state
  const [selectedCommit, setSelectedCommit] = useState<CrossProjectCommit | null>(null);
  const [diff, setDiff] = useState<string>('');
  const [diffLoading, setDiffLoading] = useState(false);
  const [expandedDiffFiles, setExpandedDiffFiles] = useState<Set<number>>(new Set());

  // Preset state
  const [activePreset, setActivePreset] = useState<Preset | null>(null);
  const [allPresets, setAllPresets] = useState<Preset[]>([]);
  const [analysisJobs, setAnalysisJobs] = useState<Map<string, AnalysisJob>>(new Map());
  const analysisQueueRef = useRef<Set<string>>(new Set());

  // Load presets list for tab bar
  const refreshPresets = useCallback(async () => {
    const presets = await presetStore.getAllPresets();
    setAllPresets(presets);
  }, []);
  useEffect(() => { refreshPresets(); }, [refreshPresets]);

  // Hydrate from reviewData (past analysis reopen)
  useEffect(() => {
    if (!reviewData) return;
    setResults(reviewData.results);
    setAllCommits(reviewData.allCommits);
    setAggregateStats(reviewData.aggregateStats);
    setCustomFrom(reviewData.customFrom);
    setCustomTo(reviewData.customTo);
    setShowSelector(false);
    setIsReview(true);
  }, [reviewData]);

  // Color mapping for repos
  const repoColorMap = useMemo(() => {
    const map = new Map<string, typeof PROJECT_COLORS[0]>();
    const selectedArray = Array.from(selectedRepoIds);
    selectedArray.forEach((id, i) => {
      map.set(id, PROJECT_COLORS[i % PROJECT_COLORS.length]);
    });
    return map;
  }, [selectedRepoIds]);

  // Sync isDark with app's dark mode class
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains('dark'));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  // Update time every second
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (selectedCommit) {
          setSelectedCommit(null);
          setDiff('');
        } else {
          onClose();
        }
        return;
      }
      if (e.key === 't' || e.key === 'T') setIsDark(d => !d);
      if (e.key === 'm' || e.key === 'M') setHideMerges(h => !h);
      if (e.key === 's' || e.key === 'S') setShowSelector(s => !s);
      if (e.key === 'f' || e.key === 'F') setShowFilters(s => !s);
      if (e.key === 'c' || e.key === 'C') setShowCharts(s => !s);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, selectedCommit]);

  const toggleRepo = useCallback((id: string) => {
    setSelectedRepoIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelectedRepoIds(new Set(availableRepos.map(r => r.id)));
  }, [availableRepos]);

  const deselectAll = useCallback(() => {
    setSelectedRepoIds(new Set());
  }, []);

  // Fetch diff for a commit — needs repo config to know which repo to query
  const fetchDiff = useCallback(async (commit: CrossProjectCommit) => {
    setDiffLoading(true);
    setDiff('');
    setExpandedDiffFiles(new Set());
    try {
      // Find the repo config for this commit
      const repo = availableRepos.find(r => r.id === commit.repoId);
      if (!repo) {
        setDiff('Could not find repository configuration for this commit.');
        return;
      }

      const config: RepoConfig = {
        type: repo.type,
        url: repo.url,
        path: repo.path,
        name: repo.name,
        hash: commit.hash,
      } as RepoConfig & { hash: string };

      // Inject auth for remote repos
      if (storedToken && (repo.type === 'bitbucket' || repo.type === 'github')) {
        config.username = storedUsername || '';
        config.appPassword = storedToken;
      }

      const data = await api.getCommitDiff(config as any, commit.hash);
      setDiff(data.diff || 'No diff available');
    } catch {
      setDiff('Error loading diff');
    } finally {
      setDiffLoading(false);
    }
  }, [availableRepos, storedUsername, storedToken]);

  const handleCommitClick = useCallback((commit: CrossProjectCommit) => {
    setSelectedCommit(commit);
    fetchDiff(commit);
  }, [fetchDiff]);

  const parsedDiffFiles = useMemo(() => {
    if (!diff) return [];
    const files: { filename: string; additions: number; deletions: number; lines: string[] }[] = [];
    const rawLines = diff.split('\n');
    let current: typeof files[0] | null = null;

    for (const line of rawLines) {
      if (line.startsWith('diff --git')) {
        // Extract filename from "diff --git a/path b/path"
        const match = line.match(/diff --git a\/(.*?) b\/(.*)/);
        const filename = match ? match[2] : line.replace('diff --git ', '');
        current = { filename, additions: 0, deletions: 0, lines: [] };
        files.push(current);
        current.lines.push(line);
      } else if (current) {
        current.lines.push(line);
        if (line.startsWith('+') && !line.startsWith('+++')) {
          current.additions++;
        } else if (line.startsWith('-') && !line.startsWith('---')) {
          current.deletions++;
        }
      }
    }
    return files;
  }, [diff]);

  const toggleDiffFile = useCallback((idx: number) => {
    setExpandedDiffFiles(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }, []);

  const expandAllDiffFiles = useCallback(() => {
    setExpandedDiffFiles(new Set(parsedDiffFiles.map((_, i) => i)));
  }, [parsedDiffFiles]);

  const collapseAllDiffFiles = useCallback(() => {
    setExpandedDiffFiles(new Set());
  }, []);

  const filteredRepos = useMemo(() => {
    const sorted = [...availableRepos].sort((a, b) => {
      const dateA = a.updatedOn ? new Date(a.updatedOn).getTime() : 0;
      const dateB = b.updatedOn ? new Date(b.updatedOn).getTime() : 0;
      return dateB - dateA;
    });
    if (!repoSearch) return sorted;
    const q = repoSearch.toLowerCase();
    return sorted.filter(r =>
      r.name.toLowerCase().includes(q) ||
      (r.description || '').toLowerCase().includes(q) ||
      (r.project || '').toLowerCase().includes(q)
    );
  }, [availableRepos, repoSearch]);

  const analyze = useCallback(async () => {
    if (selectedRepoIds.size === 0) return;

    setLoading(true);
    setError(null);
    setVisibleCount(50);
    setProgressMessage('Preparing analysis...');
    setProgressIndex(0);
    const analyzeStartTime = Date.now();
    audit.auditAnalysisStarted('cross_project', { repos: Array.from(selectedRepoIds).map(id => availableRepos.find(r => r.id === id)?.name || id) });

    try {
      const configs: RepoConfig[] = availableRepos
        .filter(r => selectedRepoIds.has(r.id))
        .map(r => {
          const config: RepoConfig = {
            type: r.type,
            url: r.url,
            path: r.path,
            name: r.name,
            repoId: r.id,
          };
          // Inject stored auth for remote repos
          if (storedToken && (r.type === 'bitbucket' || r.type === 'github')) {
            config.username = storedUsername || '';
            config.appPassword = storedToken;
          }
          // GitLab auth will be injected below after async load
          return config;
        });

      // Inject GitLab auth for gitlab repos
      const hasGitlabRepos = configs.some(c => c.type === 'gitlab');
      if (hasGitlabRepos) {
        try {
          const glSettings = await api.getGitLabSettings();
          if (glSettings?.token) {
            for (const c of configs) {
              if (c.type === 'gitlab') { c.accessToken = glSettings.token; }
            }
          }
        } catch { /* ignore */ }
      }

      setProgressTotal(configs.length);

      // Calculate weeks from date range
      const fromDate = new Date(customFrom);
      const toDate = new Date(customTo);
      const weeksFromRange = Math.max(1, Math.ceil((toDate.getTime() - fromDate.getTime()) / (1000 * 60 * 60 * 24 * 7)));

      const sessionId = `cross-project-${Date.now()}`;
      const data = await api.analyzeMultiRepo(
        configs.map(c => ({ ...c, weeks: weeksFromRange, startDate: fromDate.toISOString(), endDate: toDate.toISOString() })) as any,
        sessionId,
        (msg: string) => {
          setProgressMessage(msg);
          // Extract repo index from progress message like "[2/5] Analyzing ..."
          const m = msg.match(/^\[(\d+)\/(\d+)\]/);
          if (m) setProgressIndex(parseInt(m[1]));
        },
      );

      setResults((data.results || []) as any);
      const allCommitsFlat = data.results.flatMap((r: any) =>
        r.commits.map((c: any) => ({ ...c, repoName: r.repoName, repoId: r.repoId }))
      );
      setAllCommits(allCommitsFlat as any);
      setAggregateStats((data as any).aggregateStats || null);
      setShowSelector(false);

      const durationMs = Date.now() - analyzeStartTime;
      audit.auditAnalysisCompleted('cross_project', durationMs, { commitCount: allCommitsFlat.length });
      notifyAnalysisComplete({
        mode: 'cross_project',
        commitCount: allCommitsFlat.length,
        eventCount: 0,
        durationMs,
        success: true,
      });

      // Save to analysis history
      const repoNames = Array.from(selectedRepoIds).map(id => availableRepos.find(r => r.id === id)?.name || id);
      const snapId = historyStore.generateSnapshotId();
      historyStore.saveSnapshot(
        {
          id: snapId,
          mode: 'cross_project',
          title: `Cross-Project — ${repoNames.join(', ')}`,
          timestamp: new Date().toISOString(),
          durationMs,
          success: true,
          summary: {
            commitCount: allCommitsFlat.length,
            eventCount: 0,
            authorCount: (data as any).aggregateStats?.totalAuthors || 0,
            repoCount: selectedRepoIds.size,
            dateRange: { start: customFrom, end: customTo },
          },
        },
        {
          results: (data.results || []) as any,
          allCommits: allCommitsFlat as any,
          aggregateStats: (data as any).aggregateStats || null,
          selectedRepoNames: repoNames,
          customFrom,
          customTo,
        } satisfies CrossProjectReviewData
      ).catch(err => console.error('[analysis-history] Failed to save cross-project snapshot:', err));
    } catch (e: any) {
      setError(e.message || 'Failed to analyze repositories');
      notifyAnalysisComplete({
        mode: 'cross_project',
        commitCount: 0,
        eventCount: 0,
        durationMs: Date.now() - analyzeStartTime,
        success: false,
        error: e.message,
      });
      audit.auditAnalysisFailed('cross_project', e.message);
    } finally {
      setLoading(false);
    }
  }, [selectedRepoIds, availableRepos, storedUsername, storedToken, customFrom, customTo]);

  // ==================== PRESET HANDLERS ====================

  const handleAnalyzePreset = useCallback(async (preset: Preset) => {
    if (analysisQueueRef.current.has(preset.id)) return;
    analysisQueueRef.current.add(preset.id);

    setAnalysisJobs(prev => {
      const next = new Map(prev);
      next.set(preset.id, { presetId: preset.id, status: 'running', progress: 0, message: 'Starting...' });
      return next;
    });

    const startTime = Date.now();
    const { from: fromDate, to: toDate } = resolveTimeRange(preset.timeRange);

    try {
      const weeks = Math.max(1, Math.ceil((toDate.getTime() - fromDate.getTime()) / (1000 * 60 * 60 * 24 * 7)));
      const configs = availableRepos
        .filter(r => preset.repoIds.includes(r.id))
        .map(r => {
          const config: any = { type: r.type, url: r.url, path: r.path, name: r.name, repoId: r.id };
          if (storedToken && (r.type === 'bitbucket' || r.type === 'github')) {
            config.username = storedUsername || '';
            config.appPassword = storedToken;
          }
          return config;
        });

      const multiResult = await api.analyzeMultiRepo(
        configs.map(c => ({ ...c, weeks, startDate: fromDate.toISOString(), endDate: toDate.toISOString() })),
        `preset-${preset.id}-${Date.now()}`,
        (msg: string) => {
          const m = msg.match(/^\[(\d+)\/(\d+)\]/);
          const progress = m ? Math.round((parseInt(m[1]) / parseInt(m[2])) * 100) : 0;
          setAnalysisJobs(prev => {
            const next = new Map(prev);
            next.set(preset.id, { presetId: preset.id, status: 'running', progress, message: msg });
            return next;
          });
        },
      );

      const snapshotGitCommits = multiResult.results.flatMap((r: any) =>
        r.commits.map((c: any) => ({ ...c, repoName: r.repoName, repoId: r.repoId }))
      );

      const snapshotData: SnapshotData = {
        gitCommits: snapshotGitCommits,
        gitResults: multiResult.results,
        aggregateStats: (multiResult as any).aggregateStats,
        jiraEvents: [],
        jiraSprintSummary: [],
      };

      const meta = {
        id: crypto.randomUUID(),
        presetId: preset.id,
        resolvedFrom: fromDate.toISOString(),
        resolvedTo: toDate.toISOString(),
        analysedAt: new Date().toISOString(),
        durationMs: Date.now() - startTime,
        compressedSize: 0,
        uncompressedSize: 0,
        status: 'ready' as const,
        engineVersion: ENGINE_VERSION,
        repoCount: preset.repoIds.length,
        commitCount: snapshotGitCommits.length,
        jiraEventCount: 0,
      };

      await presetStore.saveSnapshot(meta, snapshotData);
      presetStore.putInHotCache({ meta, data: snapshotData, hydratedAt: Date.now() });

      // Also save to analysis history store for Saved Analyses tab
      const durationMs = Date.now() - startTime;
      const repoNames = preset.repoIds.map(id => availableRepos.find(r => r.id === id)?.name || id);
      const histSnapId = historyStore.generateSnapshotId();
      historyStore.saveSnapshot(
        {
          id: histSnapId,
          mode: 'cross_project',
          title: `Cross-Project — ${preset.name}`,
          timestamp: new Date().toISOString(),
          durationMs,
          success: true,
          summary: {
            commitCount: snapshotGitCommits.length,
            eventCount: 0,
            authorCount: (multiResult as any).aggregateStats?.totalAuthors || 0,
            repoCount: preset.repoIds.length,
            dateRange: { start: fromDate.toISOString(), end: toDate.toISOString() },
          },
        },
        {
          results: multiResult.results as any,
          allCommits: snapshotGitCommits as any,
          aggregateStats: (multiResult as any).aggregateStats || null,
          selectedRepoNames: repoNames,
          customFrom: toLocalDateStr(fromDate),
          customTo: toLocalDateStr(toDate),
        } satisfies CrossProjectReviewData
      ).catch(err => console.error('[analysis-history] Failed to save cross-project preset snapshot:', err));

      notifyAnalysisComplete({
        mode: 'cross_project',
        presetName: preset.name,
        commitCount: snapshotGitCommits.length,
        eventCount: 0,
        durationMs,
        success: true,
      });

      setAnalysisJobs(prev => {
        const next = new Map(prev);
        next.set(preset.id, { presetId: preset.id, status: 'done', progress: 100, message: 'Done' });
        return next;
      });
      setTimeout(() => {
        setAnalysisJobs(prev => { const next = new Map(prev); next.delete(preset.id); return next; });
      }, 2000);

    } catch (e: any) {
      notifyAnalysisComplete({
        mode: 'cross_project',
        presetName: preset.name,
        commitCount: 0,
        eventCount: 0,
        durationMs: Date.now() - startTime,
        success: false,
        error: e.message,
      });
      setAnalysisJobs(prev => {
        const next = new Map(prev);
        next.set(preset.id, { presetId: preset.id, status: 'failed', progress: 0, message: '', error: e.message });
        return next;
      });
    } finally {
      analysisQueueRef.current.delete(preset.id);
    }
  }, [availableRepos, storedUsername, storedToken]);

  const applySnapshot = useCallback((preset: Preset, snapshot: HydratedSnapshot | null) => {
    setActivePreset(preset);

    // Always sync selector state with preset config
    setSelectedRepoIds(new Set(preset.repoIds));
    const { from, to } = resolveTimeRange(preset.timeRange);
    setCustomFrom(toLocalDateStr(from));
    setCustomTo(toLocalDateStr(to));

    if (snapshot) {
      setAllCommits(snapshot.data.gitCommits || []);
      setResults((snapshot.data.gitResults || []) as any);
      setAggregateStats(snapshot.data.aggregateStats || null);
      setShowSelector(false);
    } else {
      setAllCommits([]);
      setResults([]);
      setAggregateStats(null);
      setShowSelector(true);
    }
  }, []);

  const handleOpenPreset = useCallback((preset: Preset, snapshot: HydratedSnapshot | null) => {
    applySnapshot(preset, snapshot);
    refreshPresets();
  }, [applySnapshot, refreshPresets]);

  // Quick-switch: load from hot/warm cache and swap view
  const handleSwitchPreset = useCallback(async (preset: Preset) => {
    if (activePreset?.id === preset.id) return;
    const snapshot = await presetStore.loadSnapshot(preset.id);
    applySnapshot(preset, snapshot);
  }, [activePreset?.id, applySnapshot]);

  const handleBackToPresets = useCallback(() => {
    setActivePreset(null);
    setAllCommits([]);
    setResults([]);
    setAggregateStats(null);
    setShowSelector(true);
    refreshPresets();
  }, [refreshPresets]);

  // Keyboard shortcuts: Cmd+1..9 to switch presets, Cmd+0 back to grid
  usePresetShortcuts({
    presets: allPresets,
    activePresetId: activePreset?.id ?? null,
    onSwitchPreset: handleSwitchPreset,
    onBackToGrid: handleBackToPresets,
  });

  // Auto-refresh
  useEffect(() => {
    if (!autoRefreshEnabled || loading || allCommits.length === 0) return;
    const interval = setInterval(() => {
      analyze();
    }, 60000); // Every 60 seconds
    return () => clearInterval(interval);
  }, [autoRefreshEnabled, loading, allCommits.length, analyze]);

  // Filtered commits
  const displayedCommits = useMemo(() => {
    let filtered = [...allCommits];
    if (hideMerges) {
      filtered = filtered.filter(c => !c.isMerge);
    }
    if (authorFilter.size > 0) {
      filtered = filtered.filter(c => authorFilter.has(c.author));
    }
    if (repoFilter.size > 0) {
      filtered = filtered.filter(c => repoFilter.has(c.repoId));
    }
    if (branchFilter !== 'all') {
      filtered = filtered.filter(c => c.branch === branchFilter);
    }
    if (messageSearch.trim()) {
      const q = messageSearch.toLowerCase();
      filtered = filtered.filter(c =>
        c.message.toLowerCase().includes(q) ||
        c.hash.toLowerCase().startsWith(q) ||
        c.author.toLowerCase().includes(q)
      );
    }
    if (sortOrder === 'oldest') {
      filtered.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    }
    return filtered;
  }, [allCommits, hideMerges, authorFilter, repoFilter, branchFilter, messageSearch, sortOrder]);

  // Unique authors across all repos
  const allAuthors = useMemo(() => {
    const authors = new Set(allCommits.map(c => c.author));
    return Array.from(authors).sort();
  }, [allCommits]);

  // Unique branches across all repos, sorted by most recently updated
  const allBranches = useMemo(() => {
    const latestDate = new Map<string, string>();
    for (const c of allCommits) {
      if (!c.branch) continue;
      const prev = latestDate.get(c.branch);
      if (!prev || c.date > prev) latestDate.set(c.branch, c.date);
    }
    return Array.from(latestDate.entries())
      .sort((a, b) => new Date(b[1]).getTime() - new Date(a[1]).getTime())
      .map(([branch]) => branch);
  }, [allCommits]);

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (authorFilter.size > 0) count++;
    if (repoFilter.size > 0) count++;
    if (branchFilter !== 'all') count++;
    if (messageSearch.trim()) count++;
    if (hideMerges) count++;
    if (sortOrder !== 'newest') count++;
    return count;
  }, [authorFilter, repoFilter, branchFilter, messageSearch, hideMerges, sortOrder]);

  const clearAllFilters = useCallback(() => {
    setAuthorFilter(new Set());
    setRepoFilter(new Set());
    setBranchFilter('all');
    setMessageSearch('');
    setHideMerges(false);
    setSortOrder('newest');
  }, []);

  const downloadCrossProjectPDF = useCallback(async () => {
    if (!aggregateStats || results.length === 0) return;

    try {
      // Apply alias resolution to all author stats before sending to PDF
      const aliases = getStoredGitAliases();

      const resolvedResults = results.map(r => {
        if (!r.stats) return r;
        const mergedMap = new Map<string, any>();
        for (const author of r.stats.authors) {
          const canonical = resolveGitAuthor(author.name, aliases);
          if (mergedMap.has(canonical)) {
            const ex = mergedMap.get(canonical)!;
            ex.commits += author.commits;
            ex.additions += author.additions;
            ex.deletions += author.deletions;
            ex.activeDays = Math.max(ex.activeDays, author.activeDays);
          } else {
            mergedMap.set(canonical, { ...author, name: canonical });
          }
        }
        return { ...r, stats: { ...r.stats, authors: Array.from(mergedMap.values()), totalAuthors: mergedMap.size } };
      });

      // Resolve commit author names
      const resolvedCommits = displayedCommits.map(c => ({ ...c, author: resolveGitAuthor(c.author, aliases) }));

      // Recalculate aggregate totalAuthors from merged results
      const allAuthors = new Set<string>();
      for (const r of resolvedResults) {
        if (r.stats) for (const a of r.stats.authors) allAuthors.add(a.name);
      }
      const resolvedAggregateStats = { ...aggregateStats, totalAuthors: allAuthors.size };

      generateAndDownloadReport('cross-project', {
          generatedAt: new Date().toISOString(),
          repos: resolvedResults.map(r => ({
            repoId: r.repoId,
            repoName: r.repoName,
            repoType: r.repoType,
            stats: r.stats,
            error: r.error,
          })),
          aggregateStats: resolvedAggregateStats,
          commits: resolvedCommits,
          dateRange: {
            start: new Date(customFrom).toISOString(),
            end: new Date(customTo).toISOString(),
          },
        }, `verigit-cross-project-report-${Date.now()}.pdf`);
    } catch (err) {
      console.error('Failed to download cross-project PDF:', err);
    }
  }, [aggregateStats, results, displayedCommits, customFrom, customTo]);

  // Group commits by date for the timeline
  const commitsByDate = useMemo(() => {
    const groups: Map<string, CrossProjectCommit[]> = new Map();
    for (const commit of displayedCommits.slice(0, visibleCount)) {
      const dateKey = new Date(commit.date).toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      });
      if (!groups.has(dateKey)) {
        groups.set(dateKey, []);
      }
      groups.get(dateKey)!.push(commit);
    }
    return groups;
  }, [displayedCommits, visibleCount]);

  // Chart color constants matching PROJECT_COLORS
  const CHART_HEX_COLORS = [
    '#3b82f6', '#10b981', '#f59e0b', '#a855f7', '#f43f5e',
    '#06b6d4', '#f97316', '#ec4899', '#6366f1', '#14b8a6',
  ];

  // Chart data: commits per repo (bar chart)
  const repoChartData = useMemo(() => {
    return results
      .filter(r => r.stats)
      .map((r, i) => ({
        name: r.repoName.length > 15 ? r.repoName.substring(0, 13) + '...' : r.repoName,
        fullName: r.repoName,
        commits: r.stats?.totalCommits || 0,
        additions: r.stats?.totalAdditions || 0,
        deletions: r.stats?.totalDeletions || 0,
        contributors: r.stats?.totalAuthors || 0,
        fill: CHART_HEX_COLORS[i % CHART_HEX_COLORS.length],
      }));
  }, [results]);

  // Chart data: daily activity across all repos (stacked area)
  const dailyActivityData = useMemo(() => {
    if (allCommits.length === 0) return [];
    const dayMap = new Map<string, Record<string, number>>();
    const repoNames = results.filter(r => r.stats).map(r => r.repoName);

    for (const commit of allCommits) {
      const dateKey = new Date(commit.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      if (!dayMap.has(dateKey)) {
        const entry: Record<string, number> = { date: 0 };
        repoNames.forEach(n => { entry[n] = 0; });
        entry._ts = new Date(commit.date).setHours(0, 0, 0, 0);
        dayMap.set(dateKey, entry);
      }
      const entry = dayMap.get(dateKey)!;
      entry[commit.repoName] = (entry[commit.repoName] || 0) + 1;
    }

    return Array.from(dayMap.entries())
      .map(([date, vals]) => ({ date, ...vals }))
      .sort((a: any, b: any) => a._ts - b._ts)
      .map(({ _ts, ...rest }: any) => rest);
  }, [allCommits, results]);

  // Chart data: contributor breakdown across all repos
  const contributorChartData = useMemo(() => {
    const authorMap = new Map<string, { commits: number; additions: number; deletions: number }>();
    for (const commit of allCommits) {
      if (!authorMap.has(commit.author)) {
        authorMap.set(commit.author, { commits: 0, additions: 0, deletions: 0 });
      }
      const a = authorMap.get(commit.author)!;
      a.commits++;
      a.additions += commit.additions;
      a.deletions += commit.deletions;
    }
    return Array.from(authorMap.entries())
      .map(([name, stats]) => ({
        name: name.length > 12 ? name.split(' ')[0] : name,
        fullName: name,
        ...stats,
      }))
      .sort((a, b) => b.commits - a.commits)
      .slice(0, 15);
  }, [allCommits]);

  // Chart data: repo commit share (pie)
  const repoShareData = useMemo(() => {
    return results
      .filter(r => r.stats && r.stats.totalCommits > 0)
      .map((r, i) => ({
        name: r.repoName,
        commits: r.stats?.totalCommits || 0,
        fill: CHART_HEX_COLORS[i % CHART_HEX_COLORS.length],
      }));
  }, [results]);

  // Repo names for stacked area chart
  const stackedRepoNames = useMemo(() => {
    return results.filter(r => r.stats).map(r => r.repoName);
  }, [results]);

  // Theme
  const theme = {
    bg: isDark ? 'bg-slate-950' : 'bg-gray-50',
    bgGradient: isDark
      ? 'bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950'
      : 'bg-gradient-to-br from-gray-50 via-white to-gray-100',
    text: isDark ? 'text-white' : 'text-gray-900',
    textMuted: isDark ? 'text-white/40' : 'text-gray-500',
    textSubtle: isDark ? 'text-white/30' : 'text-gray-400',
    card: isDark ? 'bg-slate-800/30 border-slate-700/30' : 'bg-white/80 border-gray-200/70 backdrop-blur-sm',
    cardHover: isDark ? 'hover:bg-slate-700/40' : 'hover:bg-gray-50',
    input: isDark ? 'bg-slate-800/40 border-slate-700/40 text-white placeholder:text-white/30' : 'bg-white/80 border-gray-300 text-gray-900 placeholder:text-gray-400',
  };

  return (
    <div className={`${inline ? 'h-[calc(100vh-4rem)]' : 'fixed inset-0 z-50'} flex flex-col overflow-hidden font-sans ${inline ? '' : theme.bg}`} style={{ WebkitAppRegion: 'no-drag' } as React.CSSProperties}>
      <NotificationPermissionDialog isDark={isDark} />
      {isReview && (
        <div className={`flex-shrink-0 px-4 py-2 text-xs font-medium text-center z-10 relative ${isDark ? 'bg-amber-500/10 text-amber-300 border-b border-amber-500/20' : 'bg-amber-50 text-amber-700 border-b border-amber-200'}`}>
          Reviewing past analysis — data is read-only
        </div>
      )}
      {/* Gradient background - hidden when inline (uses app background) */}
      {!inline && <div className={`absolute inset-0 ${theme.bgGradient}`} />}

      {/* Grid pattern - hidden when inline */}
      {!inline && <div
        className={`absolute inset-0 ${isDark ? 'opacity-[0.03]' : 'opacity-[0.5]'}`}
        style={{
          backgroundImage: isDark
            ? `linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)`
            : `linear-gradient(rgba(0,0,0,.03) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,.03) 1px, transparent 1px)`,
          backgroundSize: '40px 40px'
        }}
      />}

      {/* Main content */}
      <div className="relative flex-1 flex flex-col min-h-0">
        {/* Header - hidden when inline */}
        {!inline && (
        <header className="flex items-center justify-between mb-4 flex-shrink-0 px-6 pt-6">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-lg ${
              isDark
                ? 'bg-gradient-to-br from-blue-500 to-cyan-600 shadow-blue-500/30'
                : 'bg-gradient-to-br from-blue-500 to-cyan-600 shadow-blue-500/20'
            }`}>
              <FontAwesomeIcon icon={faLayerGroup} className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className={`text-2xl font-bold ${theme.text}`}>
                {activePreset ? activePreset.name : 'Cross-Project View'}
              </h1>
              <p className={`text-xs ${theme.textMuted}`}>
                {aggregateStats
                  ? `${aggregateStats.totalRepos} repos | ${aggregateStats.totalCommits} commits | ${aggregateStats.totalAuthors} contributors`
                  : activePreset ? 'Preset — Git Analysis' : 'Select repositories to compare activity'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Toggle selector */}
            <button
              onClick={() => setShowSelector(s => !s)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition-colors ${
                isDark ? 'bg-white/10 hover:bg-white/20' : 'bg-gray-200 hover:bg-gray-300'
              }`}
            >
              <FontAwesomeIcon icon={faFilter} className={`h-4 w-4 ${theme.text}`} />
              <span className={`text-xs font-medium ${theme.text}`}>
                {selectedRepoIds.size} selected
              </span>
            </button>

            {/* Charts toggle */}
            {allCommits.length > 0 && (
              <button
                onClick={() => setShowCharts(s => !s)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition-colors ${
                  showCharts
                    ? (isDark ? 'bg-violet-500/20' : 'bg-violet-100')
                    : (isDark ? 'bg-white/10 hover:bg-white/20' : 'bg-gray-200 hover:bg-gray-300')
                }`}
              >
                <FontAwesomeIcon icon={faChartBar} className={`h-3.5 w-3.5 ${showCharts ? (isDark ? 'text-violet-400' : 'text-violet-700') : theme.text}`} />
                <span className={`text-xs font-medium ${showCharts ? (isDark ? 'text-violet-400' : 'text-violet-700') : theme.text}`}>
                  Charts
                </span>
              </button>
            )}

            {/* PDF Report */}
            {allCommits.length > 0 && (
              <button
                onClick={downloadCrossProjectPDF}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition-colors ${
                  isDark ? 'bg-white/10 hover:bg-white/20' : 'bg-gray-200 hover:bg-gray-300'
                }`}
              >
                <FontAwesomeIcon icon={faDownload} className={`h-3.5 w-3.5 ${theme.text}`} />
                <span className={`text-xs font-medium ${theme.text}`}>
                  PDF Report
                </span>
              </button>
            )}

            {/* Auto-refresh toggle */}
            {allCommits.length > 0 && (
              <button
                onClick={() => setAutoRefreshEnabled(a => !a)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition-colors ${
                  autoRefreshEnabled
                    ? (isDark ? 'bg-green-500/20' : 'bg-green-100')
                    : (isDark ? 'bg-white/10 hover:bg-white/20' : 'bg-gray-200 hover:bg-gray-300')
                }`}
              >
                <FontAwesomeIcon icon={faRotate} className={`h-3.5 w-3.5 ${autoRefreshEnabled ? 'text-green-400 animate-spin' : theme.text}`} style={autoRefreshEnabled ? { animationDuration: '3s' } : {}} />
                <span className={`text-xs font-medium ${autoRefreshEnabled ? (isDark ? 'text-green-400' : 'text-green-700') : theme.text}`}>
                  {autoRefreshEnabled ? 'Auto' : 'Manual'}
                </span>
              </button>
            )}

            {/* Active filter indicator */}
            {activeFilterCount > 0 && (
              <button
                onClick={clearAllFilters}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${
                  isDark ? 'bg-cyan-500/20' : 'bg-cyan-100'
                }`}
              >
                <FontAwesomeIcon icon={faFilter} className={`h-3 w-3 ${isDark ? 'text-cyan-400' : 'text-cyan-700'}`} />
                <span className={`text-xs font-medium ${isDark ? 'text-cyan-400' : 'text-cyan-700'}`}>
                  {activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''}
                </span>
                <FontAwesomeIcon icon={faXmark} className={`h-3 w-3 ${isDark ? 'text-cyan-400/60' : 'text-cyan-600'}`} />
              </button>
            )}

            {/* Live indicator */}
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${
              isDark ? 'bg-green-500/20' : 'bg-green-100'
            }`}>
              <div className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
              </div>
              <span className={`text-xs font-medium ${isDark ? 'text-green-400' : 'text-green-700'}`}>
                {loading ? 'Analyzing...' : 'LIVE'}
              </span>
            </div>

            {/* Time */}
            <div className="text-right">
              <div className={`text-3xl font-light tracking-tight ${theme.text}`} style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
                {currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
              </div>
              <div className={`text-xs ${theme.textMuted}`}>
                {currentTime.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
              </div>
            </div>

            {/* Theme toggle */}
            <button
              onClick={() => setIsDark(!isDark)}
              className={`p-2 rounded-full transition-colors ${isDark ? 'bg-white/10 hover:bg-white/20' : 'bg-gray-200 hover:bg-gray-300'}`}
              title="Toggle theme (T)"
            >
              {isDark ? <FontAwesomeIcon icon={faSun} className="h-5 w-5 text-amber-400" /> : <FontAwesomeIcon icon={faMoon} className="h-5 w-5 text-slate-600" />}
            </button>

            {/* Close */}
            <button
              onClick={onClose}
              className={`p-2 rounded-full transition-colors ${isDark ? 'bg-white/10 hover:bg-white/20' : 'bg-gray-200 hover:bg-gray-300'}`}
              title="Exit (Esc)"
            >
              <FontAwesomeIcon icon={faXmark} className={`h-5 w-5 ${isDark ? 'text-white/80' : 'text-gray-600'}`} />
            </button>
          </div>
        </header>
        )}

        {/* Inline toolbar — shown when header is hidden (inline mode) */}
        {inline && (
          <div className="flex items-center justify-between px-6 pt-4 pb-2 flex-shrink-0">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowSelector(s => !s)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition-colors ${isDark ? 'bg-white/10 hover:bg-white/20' : 'bg-gray-200 hover:bg-gray-300'}`}
              >
                <FontAwesomeIcon icon={faFilter} className={`h-3.5 w-3.5 ${theme.text}`} />
                <span className={`text-xs font-medium ${theme.text}`}>{selectedRepoIds.size} repos</span>
              </button>
              {allCommits.length > 0 && (
                <button
                  onClick={() => setShowCharts(s => !s)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition-colors ${showCharts ? (isDark ? 'bg-violet-500/20' : 'bg-violet-100') : (isDark ? 'bg-white/10 hover:bg-white/20' : 'bg-gray-200 hover:bg-gray-300')}`}
                >
                  <FontAwesomeIcon icon={faChartBar} className={`h-3.5 w-3.5 ${showCharts ? (isDark ? 'text-violet-400' : 'text-violet-700') : theme.text}`} />
                  <span className={`text-xs font-medium ${showCharts ? (isDark ? 'text-violet-400' : 'text-violet-700') : theme.text}`}>Charts</span>
                </button>
              )}
              {allCommits.length > 0 && (
                <button
                  onClick={downloadCrossProjectPDF}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition-colors ${isDark ? 'bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300' : 'bg-emerald-100 hover:bg-emerald-200 text-emerald-700'}`}
                >
                  <FontAwesomeIcon icon={faDownload} className="h-3.5 w-3.5" />
                  <span className="text-xs font-medium">PDF Report</span>
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              {aggregateStats && (
                <span className={`text-xs ${theme.textMuted}`}>
                  {aggregateStats.totalRepos} repos · {aggregateStats.totalCommits} commits · {aggregateStats.totalAuthors} contributors
                </span>
              )}
            </div>
          </div>
        )}

        {/* Preset tab bar — shown only when viewing a preset */}
        {allPresets.length > 0 && activePreset && (
          <div className={`flex-shrink-0 flex items-center gap-1 px-4 py-1.5 border-b ${isDark ? 'border-slate-700/30 bg-slate-900/30' : 'border-slate-200 bg-slate-50/80'}`}>
            <button
              onClick={handleBackToPresets}
              className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                !activePreset
                  ? (isDark ? 'bg-blue-500/20 text-blue-300 ring-1 ring-blue-500/30' : 'bg-blue-100 text-blue-700 ring-1 ring-blue-300')
                  : (isDark ? 'text-white/40 hover:text-white/60 hover:bg-slate-700/40' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100')
              }`}
            >
              <FontAwesomeIcon icon={faLayerGroup} className="h-3 w-3" />
              All Presets
            </button>
            <div className={`w-px h-4 mx-1 ${isDark ? 'bg-white/10' : 'bg-slate-300'}`} />
            {allPresets.map(preset => {
              const isActive = activePreset?.id === preset.id;
              return (
                <button
                  key={preset.id}
                  onClick={() => handleSwitchPreset(preset)}
                  className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                    isActive
                      ? (isDark ? 'bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/30 shadow-sm' : 'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-300 shadow-sm')
                      : (isDark ? 'text-white/40 hover:text-white/60 hover:bg-slate-700/40' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100')
                  }`}
                >
                  <span className="truncate max-w-[140px]">{preset.name}</span>
                  {isActive && <FontAwesomeIcon icon={faCircleCheck} className="h-2.5 w-2.5 flex-shrink-0" />}
                </button>
              );
            })}
          </div>
        )}

        {/* Aggregate stats row */}
        {aggregateStats && (
          <div className="grid grid-cols-5 gap-3 mb-4 flex-shrink-0 px-6">
            <StatCard value={aggregateStats.totalRepos} label="Repositories" icon={<FontAwesomeIcon icon={faLayerGroup} className="h-4 w-4" />} color="blue" isDark={isDark} />
            <StatCard value={aggregateStats.totalCommits} label="Total Commits" icon={<FontAwesomeIcon icon={faCodeCommit} className="h-4 w-4" />} color="indigo" isDark={isDark} />
            <StatCard value={aggregateStats.totalAuthors} label="Contributors" icon={<FontAwesomeIcon icon={faUsers} className="h-4 w-4" />} color="violet" isDark={isDark} />
            <StatCard value={`+${formatNumber(aggregateStats.totalAdditions)}`} label="Additions" icon={<FontAwesomeIcon icon={faArrowTrendUp} className="h-4 w-4" />} color="emerald" isDark={isDark} />
            <StatCard value={`-${formatNumber(aggregateStats.totalDeletions)}`} label="Deletions" icon={<FontAwesomeIcon icon={faArrowTrendUp} className="h-4 w-4 rotate-180" />} color="rose" isDark={isDark} />
          </div>
        )}

        {/* Main area */}
        <div className="flex-1 flex min-h-0 overflow-hidden">
          {/* Project Selector Panel */}
          {showSelector && (
            <div className="w-80 border-r border-gray-200/70 dark:border-slate-700/40 p-4 flex flex-col flex-shrink-0 overflow-hidden bg-white/50 dark:bg-slate-900/40 backdrop-blur-xl">
              <div className="flex items-center justify-between mb-3">
                <h2 className={`text-sm font-semibold ${theme.text}`}>Select Repositories</h2>
                <div className="flex gap-1">
                  <button
                    onClick={selectAll}
                    className={`text-xs px-2 py-1 rounded ${isDark ? 'bg-white/10 hover:bg-white/20 text-white/60' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`}
                  >
                    All
                  </button>
                  <button
                    onClick={deselectAll}
                    className={`text-xs px-2 py-1 rounded ${isDark ? 'bg-white/10 hover:bg-white/20 text-white/60' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`}
                  >
                    None
                  </button>
                </div>
              </div>

              {/* Search */}
              {availableRepos.length > 8 && (
                <input
                  type="text"
                  value={repoSearch}
                  onChange={(e) => setRepoSearch(e.target.value)}
                  placeholder="Search repos..."
                  className={`w-full px-3 py-1.5 text-xs rounded-lg border mb-3 outline-none focus:ring-1 focus:ring-blue-500 ${theme.input}`}
                />
              )}

              {/* Repo list */}
              <div className="flex-1 overflow-y-auto space-y-1 min-h-0">
                {filteredRepos.map(repo => {
                  const isSelected = selectedRepoIds.has(repo.id);
                  const color = repoColorMap.get(repo.id);
                  return (
                    <button
                      key={repo.id}
                      onClick={() => toggleRepo(repo.id)}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left transition-all ${
                        isSelected
                          ? (isDark ? 'bg-slate-700/40 border border-slate-600/40' : 'bg-blue-50 border border-blue-200')
                          : (isDark ? 'hover:bg-slate-700/30 border border-transparent' : 'hover:bg-gray-50 border border-transparent')
                      }`}
                    >
                      {isSelected ? (
                        <FontAwesomeIcon icon={faSquareCheck} className={`h-4 w-4 flex-shrink-0 ${color?.text || 'text-blue-400'}`} />
                      ) : (
                        <FontAwesomeIcon icon={faSquare} className={`h-4 w-4 flex-shrink-0 ${isDark ? 'text-white/20' : 'text-gray-300'}`} />
                      )}
                      {color && isSelected && (
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${color.dot}`} />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className={`text-xs font-medium truncate ${isSelected ? theme.text : (isDark ? 'text-white/60' : 'text-gray-600')}`}>
                          {repo.icon ? `${repo.icon} ` : ''}{repo.name}
                        </div>
                        {repo.project && (
                          <div className={`text-[10px] truncate ${theme.textMuted}`}>{repo.project}</div>
                        )}
                      </div>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0 ${
                        isDark ? 'bg-white/5 text-white/30' : 'bg-gray-100 text-gray-400'
                      }`}>
                        {repo.type}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Date Range Picker */}
              <div className={`mt-3 rounded-lg border p-3 ${isDark ? 'bg-slate-800/30 border-slate-700/30' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <FontAwesomeIcon icon={faCalendarDays} className={`h-3.5 w-3.5 ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`} />
                  <span className={`text-xs font-semibold ${theme.text}`}>Time Range</span>
                </div>
                {/* Preset buttons */}
                <div className="flex flex-wrap gap-1 mb-2">
                  {[
                    {
                      label: 'Friday -> Monday (now)',
                      getValue: () => {
                        const now = new Date();
                        return {
                          from: previousFriday(now),
                          to: now
                        };
                      }
                    },
                    { label: 'Yesterday', days: -1 },
                    { label: '24h', days: 1 },
                    { label: '7d', days: 7 },
                    { label: '2w', days: 14 },
                    { label: '4w', days: 28 },
                    { label: '2m', days: 60 },
                    { label: '3m', days: 90 },
                    { label: '6m', days: 180 },
                    { label: 'All Time', days: -2 },
                  ].map(preset => (
                    <button
                      key={preset.label}
                      onClick={() => {
                        setDateRangePreset(preset.getValue ? 'friday-monday' : (preset.days || 0));
                        let from: Date;
                        let to: Date;

                        if (preset.getValue) {
                          const value = preset.getValue();
                          from = value.from;
                          to = value.to;
                        } else if (preset.days === -1) {
                          from = subDays(new Date(), 1);
                          from.setHours(0, 0, 0, 0);
                          to = subDays(new Date(), 1);
                          to.setHours(23, 59, 59, 999);
                        } else if (preset.days === -2) {
                          from = new Date();
                          from.setFullYear(from.getFullYear() - 10);
                          to = new Date();
                        } else {
                          to = new Date();
                          from = subDays(to, preset.days);
                        }
                        setCustomFrom(toLocalDateStr(from));
                        setCustomTo(toLocalDateStr(to));
                        setShowDatePicker(false);
                      }}
                      className={`px-2 py-1 text-[10px] font-medium rounded transition-colors ${
                        (preset.getValue ? dateRangePreset === 'friday-monday' : dateRangePreset === preset.days) && !showDatePicker
                          ? (isDark ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30' : 'bg-cyan-100 text-cyan-700 border border-cyan-300')
                          : (isDark ? 'bg-white/5 text-white/50 hover:bg-white/10 border border-transparent' : 'bg-white text-gray-500 hover:bg-gray-100 border border-transparent')
                      }`}
                    >
                      {preset.label}
                    </button>
                  ))}
                  <button
                    onClick={() => setShowDatePicker(s => !s)}
                    className={`px-2 py-1 text-[10px] font-medium rounded transition-colors ${
                      showDatePicker
                        ? (isDark ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30' : 'bg-cyan-100 text-cyan-700 border border-cyan-300')
                        : (isDark ? 'bg-white/5 text-white/50 hover:bg-white/10 border border-transparent' : 'bg-white text-gray-500 hover:bg-gray-100 border border-transparent')
                    }`}
                  >
                    Custom
                  </button>
                </div>
                {/* Custom date inputs */}
                {showDatePicker && (
                  <div className="flex gap-2 items-center">
                    <input
                      type="date"
                      value={customFrom}
                      onChange={(e) => { setCustomFrom(e.target.value); setDateRangePreset(0); }}
                      max={customTo}
                      className={`flex-1 px-2 py-1 text-[10px] rounded border outline-none ${theme.input}`}
                    />
                    <span className={`text-[10px] ${theme.textMuted}`}>to</span>
                    <input
                      type="date"
                      value={customTo}
                      onChange={(e) => { setCustomTo(e.target.value); setDateRangePreset(0); }}
                      min={customFrom}
                      max={toLocalDateStr()}
                      className={`flex-1 px-2 py-1 text-[10px] rounded border outline-none ${theme.input}`}
                    />
                  </div>
                )}
                {/* Display selected range */}
                <div className={`text-[10px] mt-1.5 ${theme.textMuted}`}>
                  {new Date(customFrom).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  {' — '}
                  {new Date(customTo).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  {' '}
                  ({Math.ceil((new Date(customTo).getTime() - new Date(customFrom).getTime()) / (1000 * 60 * 60 * 24))} days)
                </div>
              </div>

              {/* Analyze button */}
              <button
                onClick={analyze}
                disabled={selectedRepoIds.size === 0 || loading}
                className={`mt-3 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${
                  selectedRepoIds.size === 0 || loading
                    ? (isDark ? 'bg-white/5 text-white/20 cursor-not-allowed' : 'bg-gray-100 text-gray-400 cursor-not-allowed')
                    : 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white hover:from-blue-600 hover:to-cyan-600 shadow-lg shadow-blue-500/25'
                }`}
              >
                {loading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faCode} className="h-4 w-4" />
                    Analyze {selectedRepoIds.size} {selectedRepoIds.size === 1 ? 'repo' : 'repos'}
                  </>
                )}
              </button>
              {loading && (
                <div className="mt-3 space-y-2">
                  <div className="flex items-center justify-between text-[10px]">
                    <span className={isDark ? 'text-white/50' : 'text-slate-500'}>{progressIndex}/{progressTotal} repos</span>
                    <span className={isDark ? 'text-cyan-400' : 'text-cyan-600'}>{progressTotal > 0 ? Math.round((progressIndex / progressTotal) * 100) : 0}%</span>
                  </div>
                  <div className={`w-full h-1.5 rounded-full overflow-hidden ${isDark ? 'bg-white/10' : 'bg-slate-200'}`}>
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 transition-all duration-500"
                      style={{ width: `${progressTotal > 0 ? (progressIndex / progressTotal) * 100 : 0}%` }}
                    />
                  </div>
                  <p className={`text-[10px] truncate ${isDark ? 'text-white/40' : 'text-slate-400'}`}>{progressMessage}</p>
                </div>
              )}
            </div>
          )}

          {/* Timeline / Main Content */}
          <div className="flex-1 flex flex-col min-h-0 px-6 pb-6">
            {error && (
              <div className={`mb-4 p-3 rounded-lg border ${isDark ? 'bg-red-500/10 border-red-500/20 text-red-400' : 'bg-red-50 border-red-200 text-red-600'}`}>
                <p className="text-sm">{error}</p>
              </div>
            )}

            {/* Per-repo summary cards (clickable to filter) — hidden when expanded */}
            {!expandTimeline && results.length > 0 && (
              <div className="flex gap-2 mb-3 flex-wrap flex-shrink-0">
                {results.filter(r => !r.error).map(r => {
                  const color = repoColorMap.get(r.repoId);
                  const isFiltered = repoFilter.size > 0 && !repoFilter.has(r.repoId);
                  return (
                    <button
                      key={r.repoId}
                      onClick={() => {
                        setRepoFilter(prev => {
                          const next = new Set(prev);
                          if (next.has(r.repoId)) {
                            next.delete(r.repoId);
                          } else {
                            next.add(r.repoId);
                          }
                          return next;
                        });
                      }}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs transition-all cursor-pointer ${
                        isFiltered
                          ? (isDark ? 'bg-slate-800/20 border-slate-700/20 text-white/20 opacity-40' : 'bg-gray-50 border-gray-100 text-gray-300 opacity-50')
                          : (isDark ? `${color?.light || 'bg-white/5'} ${color?.border || 'border-white/10'} ${color?.text || 'text-white/60'}` : `bg-gray-50 border-gray-200 text-gray-700`)
                      }`}
                    >
                      {color && <div className={`w-2 h-2 rounded-full ${color.dot} ${isFiltered ? 'opacity-30' : ''}`} />}
                      <span className="font-medium">{r.repoName}</span>
                      <span className="opacity-70">{r.stats?.totalCommits || 0}</span>
                    </button>
                  );
                })}
                {repoFilter.size > 0 && (
                  <button
                    onClick={() => setRepoFilter(new Set())}
                    className={`flex items-center gap-1 px-2 py-1.5 rounded-full text-[10px] transition-colors ${
                      isDark ? 'text-white/30 hover:text-white/60' : 'text-gray-400 hover:text-gray-600'
                    }`}
                  >
                    <FontAwesomeIcon icon={faCircleXmark} className="h-3 w-3" />
                    Clear
                  </button>
                )}
              </div>
            )}

            {/* Filter Bar — hidden when expanded */}
            {!expandTimeline && allCommits.length > 0 && (
              <div className={`mb-3 flex-shrink-0 rounded-lg border p-3 ${theme.card}`}>
                {/* Filter header */}
                <div className="flex items-center justify-between mb-2">
                  <button
                    onClick={() => setShowFilters(s => !s)}
                    className="flex items-center gap-2"
                  >
                    <FontAwesomeIcon icon={faFilter} className={`h-3.5 w-3.5 ${isDark ? 'text-white/40' : 'text-gray-500'}`} />
                    <span className={`text-xs font-semibold ${theme.text}`}>Filters</span>
                    {activeFilterCount > 0 && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                        isDark ? 'bg-cyan-500/20 text-cyan-400' : 'bg-cyan-100 text-cyan-700'
                      }`}>
                        {activeFilterCount} active
                      </span>
                    )}
                    {showFilters ? (
                      <FontAwesomeIcon icon={faChevronUp} className={`h-3 w-3 ${theme.textMuted}`} />
                    ) : (
                      <FontAwesomeIcon icon={faChevronDown} className={`h-3 w-3 ${theme.textMuted}`} />
                    )}
                  </button>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs ${theme.textMuted}`}>
                      {displayedCommits.length} of {allCommits.length} commits
                    </span>
                    {activeFilterCount > 0 && (
                      <button
                        onClick={clearAllFilters}
                        className={`text-[10px] px-2 py-0.5 rounded transition-colors ${
                          isDark ? 'text-red-400 hover:bg-red-500/10' : 'text-red-600 hover:bg-red-50'
                        }`}
                      >
                        Clear all
                      </button>
                    )}
                  </div>
                </div>

                {showFilters && (
                  <div className="space-y-2.5">
                    {/* Row 1: Search + Sort + Merges */}
                    <div className="flex gap-2 items-center">
                      <div className={`flex-1 flex items-center gap-2 px-2.5 py-1.5 rounded-lg border ${theme.input}`}>
                        <FontAwesomeIcon icon={faMagnifyingGlass} className={`h-3.5 w-3.5 flex-shrink-0 ${isDark ? 'text-white/30' : 'text-gray-400'}`} />
                        <input
                          type="text"
                          placeholder="Search commits, authors, hashes..."
                          value={messageSearch}
                          onChange={(e) => setMessageSearch(e.target.value)}
                          className={`flex-1 bg-transparent outline-none text-xs ${theme.text} placeholder:${isDark ? 'text-white/20' : 'text-gray-400'}`}
                        />
                        {messageSearch && (
                          <button onClick={() => setMessageSearch('')}>
                            <FontAwesomeIcon icon={faXmark} className={`h-3 w-3 ${theme.textMuted}`} />
                          </button>
                        )}
                      </div>
                      <button
                        onClick={() => setSortOrder(s => s === 'newest' ? 'oldest' : 'newest')}
                        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs transition-colors ${
                          sortOrder === 'oldest'
                            ? (isDark ? 'bg-amber-500/10 border-amber-500/20 text-amber-400' : 'bg-amber-50 border-amber-200 text-amber-700')
                            : (isDark ? 'bg-white/5 border-white/10 text-white/50 hover:bg-white/10' : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50')
                        }`}
                      >
                        <FontAwesomeIcon icon={faArrowsUpDown} className="h-3 w-3" />
                        {sortOrder === 'newest' ? 'Newest' : 'Oldest'}
                      </button>
                      <button
                        onClick={() => setHideMerges(h => !h)}
                        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs transition-colors ${
                          hideMerges
                            ? (isDark ? 'bg-orange-500/10 border-orange-500/20 text-orange-400' : 'bg-orange-50 border-orange-200 text-orange-700')
                            : (isDark ? 'bg-white/5 border-white/10 text-white/50 hover:bg-white/10' : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50')
                        }`}
                      >
                        <FontAwesomeIcon icon={faCodeCommit} className="h-3 w-3" />
                        {hideMerges ? 'Merges hidden' : 'Show merges'}
                      </button>
                    </div>

                    {/* Row 2: Branch filter */}
                    {allBranches.length > 0 && (
                      <div className="flex items-center gap-2">
                        <FontAwesomeIcon icon={faCodeBranch} className={`h-3.5 w-3.5 flex-shrink-0 ${isDark ? 'text-white/30' : 'text-gray-400'}`} />
                        <span className={`text-[10px] font-medium flex-shrink-0 ${theme.textMuted}`}>Branch:</span>
                        <select
                          value={branchFilter}
                          onChange={(e) => setBranchFilter(e.target.value)}
                          className={`text-xs px-2 py-1 rounded border outline-none ${theme.input}`}
                        >
                          <option value="all">All branches ({allBranches.length})</option>
                          {allBranches.map(b => (
                            <option key={b} value={b}>{b}</option>
                          ))}
                        </select>
                        {branchFilter !== 'all' && (
                          <button
                            onClick={() => setBranchFilter('all')}
                            className={`p-0.5 rounded ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
                          >
                            <FontAwesomeIcon icon={faXmark} className={`h-3 w-3 ${theme.textMuted}`} />
                          </button>
                        )}
                      </div>
                    )}

                    {/* Row 3: Contributors multi-select */}
                    {allAuthors.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 mb-1.5">
                          <FontAwesomeIcon icon={faUsers} className={`h-3.5 w-3.5 flex-shrink-0 ${isDark ? 'text-white/30' : 'text-gray-400'}`} />
                          <span className={`text-[10px] font-medium ${theme.textMuted}`}>
                            Contributors: {authorFilter.size > 0 ? `${authorFilter.size} of ${allAuthors.length}` : `All (${allAuthors.length})`}
                          </span>
                          {authorFilter.size > 0 && (
                            <button
                              onClick={() => setAuthorFilter(new Set())}
                              className={`text-[10px] px-1.5 py-0.5 rounded transition-colors ${
                                isDark ? 'text-white/30 hover:text-white/60 hover:bg-white/5' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                              }`}
                            >
                              Clear
                            </button>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {allAuthors.map(author => {
                            const isSelected = authorFilter.has(author);
                            const commitCount = allCommits.filter(c => c.author === author).length;
                            return (
                              <button
                                key={author}
                                onClick={() => {
                                  setAuthorFilter(prev => {
                                    const next = new Set(prev);
                                    if (next.has(author)) {
                                      next.delete(author);
                                    } else {
                                      next.add(author);
                                    }
                                    return next;
                                  });
                                }}
                                className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium transition-all ${
                                  authorFilter.size === 0
                                    ? (isDark ? 'bg-white/5 text-white/50 hover:bg-white/10 border border-white/10' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200')
                                    : isSelected
                                      ? (isDark ? 'bg-indigo-500/20 text-indigo-400 border border-indigo-500/30' : 'bg-indigo-100 text-indigo-700 border border-indigo-300')
                                      : (isDark ? 'bg-slate-800/20 text-white/20 hover:bg-slate-700/30 border border-slate-700/20' : 'bg-gray-50 text-gray-300 hover:bg-gray-100 border border-gray-100')
                                }`}
                              >
                                <span>{author.length > 20 ? author.split(' ')[0] : author}</span>
                                <span className="opacity-60">{commitCount}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Charts Section — hidden when expanded */}
            {!expandTimeline && showCharts && allCommits.length > 0 && (
              <div className="mb-4 flex-shrink-0">
                <div className="grid grid-cols-2 gap-3">
                  {/* Commits per Repo */}
                  <div className={`rounded-xl border p-4 ${theme.card}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <FontAwesomeIcon icon={faChartBar} className={`h-3.5 w-3.5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                      <h3 className={`text-xs font-semibold ${theme.text}`}>Commits per Repository</h3>
                    </div>
                    <ResponsiveContainer width="100%" height={180}>
                      <BarChart data={repoChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.1)'} />
                        <XAxis dataKey="name" tick={{ fontSize: 10, fill: isDark ? 'rgba(255,255,255,0.4)' : '#6b7280' }} />
                        <YAxis tick={{ fontSize: 10, fill: isDark ? 'rgba(255,255,255,0.4)' : '#6b7280' }} />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: isDark ? '#1e293b' : '#fff',
                            border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}`,
                            borderRadius: '8px',
                            fontSize: '12px',
                            color: isDark ? '#fff' : '#111',
                          }}
                          formatter={(value: number, _name: string) => [value, 'Commits']}
                          labelFormatter={(label: string) => {
                            const item = repoChartData.find(d => d.name === label);
                            return item?.fullName || label;
                          }}
                        />
                        <Bar dataKey="commits" radius={[4, 4, 0, 0]}>
                          {repoChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Daily Activity (Stacked Area per Repo) */}
                  <div className={`rounded-xl border p-4 ${theme.card}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <FontAwesomeIcon icon={faArrowTrendUp} className={`h-3.5 w-3.5 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
                      <h3 className={`text-xs font-semibold ${theme.text}`}>Daily Activity by Repository</h3>
                    </div>
                    <ResponsiveContainer width="100%" height={180}>
                      <AreaChart data={dailyActivityData}>
                        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.1)'} />
                        <XAxis dataKey="date" tick={{ fontSize: 10, fill: isDark ? 'rgba(255,255,255,0.4)' : '#6b7280' }} />
                        <YAxis tick={{ fontSize: 10, fill: isDark ? 'rgba(255,255,255,0.4)' : '#6b7280' }} />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: isDark ? '#1e293b' : '#fff',
                            border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}`,
                            borderRadius: '8px',
                            fontSize: '12px',
                            color: isDark ? '#fff' : '#111',
                          }}
                        />
                        {stackedRepoNames.map((name, i) => (
                          <Area
                            key={name}
                            type="monotone"
                            dataKey={name}
                            stackId="1"
                            stroke={CHART_HEX_COLORS[i % CHART_HEX_COLORS.length]}
                            fill={CHART_HEX_COLORS[i % CHART_HEX_COLORS.length]}
                            fillOpacity={0.3}
                          />
                        ))}
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Commits by Contributor */}
                  <div className={`rounded-xl border p-4 ${theme.card}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <FontAwesomeIcon icon={faUsers} className={`h-3.5 w-3.5 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                      <h3 className={`text-xs font-semibold ${theme.text}`}>Top Contributors (All Repos)</h3>
                    </div>
                    <ResponsiveContainer width="100%" height={180}>
                      <BarChart data={contributorChartData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.1)'} />
                        <XAxis type="number" tick={{ fontSize: 10, fill: isDark ? 'rgba(255,255,255,0.4)' : '#6b7280' }} />
                        <YAxis dataKey="name" type="category" tick={{ fontSize: 10, fill: isDark ? 'rgba(255,255,255,0.4)' : '#6b7280' }} width={70} />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: isDark ? '#1e293b' : '#fff',
                            border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}`,
                            borderRadius: '8px',
                            fontSize: '12px',
                            color: isDark ? '#fff' : '#111',
                          }}
                          labelFormatter={(label: string) => {
                            const item = contributorChartData.find(d => d.name === label);
                            return item?.fullName || label;
                          }}
                        />
                        <Bar dataKey="commits" fill="#a855f7" name="Commits" radius={[0, 4, 4, 0]} />
                        <Bar dataKey="additions" fill="#10b981" name="Additions" radius={[0, 4, 4, 0]} />
                        <Bar dataKey="deletions" fill="#f43f5e" name="Deletions" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Repo Commit Share (Pie) */}
                  <div className={`rounded-xl border p-4 ${theme.card}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <FontAwesomeIcon icon={faCodeCommit} className={`h-3.5 w-3.5 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
                      <h3 className={`text-xs font-semibold ${theme.text}`}>Commit Share by Repository</h3>
                    </div>
                    <ResponsiveContainer width="100%" height={180}>
                      <PieChart>
                        <Pie
                          data={repoShareData}
                          dataKey="commits"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={65}
                          label={({ name, percent }) => `${name.length > 10 ? name.substring(0, 8) + '..' : name} ${(percent * 100).toFixed(0)}%`}
                          labelLine={false}
                          style={{ fontSize: '10px' }}
                        >
                          {repoShareData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{
                            backgroundColor: isDark ? '#1e293b' : '#fff',
                            border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}`,
                            borderRadius: '8px',
                            fontSize: '12px',
                            color: isDark ? '#fff' : '#111',
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            )}

            {/* Loading state */}
            {loading && allCommits.length === 0 && (
              <div className="flex-1 flex items-center justify-center py-12 px-6">
                <div className={`w-full max-w-md rounded-2xl border p-8 ${isDark ? 'bg-slate-900/60 border-white/10' : 'bg-white border-slate-200 shadow-lg'}`}>
                  {/* Animated icon */}
                  <div className="flex justify-center mb-6">
                    <div className="relative">
                      <div className={`h-16 w-16 rounded-full flex items-center justify-center ${isDark ? 'bg-blue-500/10' : 'bg-blue-50'}`}>
                        <FontAwesomeIcon icon={faSpinner} className="h-8 w-8 text-blue-500 animate-spin" />
                      </div>
                      <div className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                        <span className="text-[9px] font-bold text-white">{progressIndex}</span>
                      </div>
                    </div>
                  </div>

                  <h3 className={`text-center text-lg font-semibold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>Analyzing Repositories</h3>
                  <p className={`text-center text-xs mb-6 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>This may take a moment for large projects</p>

                  {progressTotal > 0 && (
                    <div className="space-y-3 mb-4">
                      <div className="flex items-center justify-between">
                        <span className={`text-xs font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Repositories</span>
                        <span className={`text-xs font-mono font-bold ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`}>{progressIndex} / {progressTotal}</span>
                      </div>
                      <div className={`w-full h-2.5 rounded-full overflow-hidden ${isDark ? 'bg-white/5' : 'bg-slate-100'}`}>
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-blue-500 via-blue-400 to-cyan-400 transition-all duration-700 ease-out"
                          style={{ width: `${Math.max(2, (progressIndex / progressTotal) * 100)}%` }}
                        />
                      </div>
                      <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${isDark ? 'bg-slate-800/30' : 'bg-slate-50'}`}>
                        <div className="h-1.5 w-1.5 rounded-full bg-blue-500 animate-pulse flex-shrink-0" />
                        <p className={`text-[11px] truncate ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{progressMessage || 'Preparing...'}</p>
                      </div>
                    </div>
                  )}

                  {progressTotal > 0 && (
                    <div className="mt-5 text-center">
                      <span className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                        {Math.round((progressIndex / progressTotal) * 100)}%
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Preset grid — shown when no data and no active preset */}
            {allCommits.length === 0 && !loading && !activePreset && (
              <PresetGrid
                availableRepos={availableRepos}
                jiraProjects={[]}
                hasJira={false}
                isDark={isDark}
                onOpenPreset={handleOpenPreset}
                onAnalyzePreset={handleAnalyzePreset}
                analysisJobs={analysisJobs}
                onPresetsChanged={refreshPresets}
              />
            )}

            {/* Empty state — active preset but no data */}
            {allCommits.length === 0 && !loading && activePreset && (
              <div className={`flex-1 flex items-center justify-center ${theme.textMuted}`}>
                <div className="text-center">
                  <FontAwesomeIcon icon={faLayerGroup} className="h-16 w-16 mx-auto mb-4 opacity-30" />
                  <p className="text-lg font-medium mb-2">{activePreset.name}</p>
                  <p className="text-sm mb-4">No cached snapshot. Select repos and click Analyze.</p>
                  <button onClick={handleBackToPresets}
                    className={`text-xs px-3 py-1.5 rounded-lg transition-colors ${isDark ? 'bg-slate-800 hover:bg-slate-700 text-slate-400' : 'bg-slate-100 hover:bg-slate-200 text-slate-500'}`}>
                    ← Back to Presets
                  </button>
                </div>
              </div>
            )}

            {/* Timeline */}
            {allCommits.length > 0 && !loading && (
              <div className="flex-1 flex flex-col min-h-0">
                {/* Timeline header with expand button */}
                <div className={`flex items-center justify-between px-4 py-2 rounded-t-xl border border-b-0 flex-shrink-0 ${theme.card}`}>
                  <span className={`text-xs font-semibold ${theme.text}`}>
                    Commits {displayedCommits.length !== allCommits.length ? `(${displayedCommits.length} filtered)` : `(${allCommits.length})`}
                  </span>
                  <button
                    onClick={() => setExpandTimeline(e => !e)}
                    className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-medium transition-colors ${
                      expandTimeline
                        ? (isDark ? 'bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/30' : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200')
                        : (isDark ? 'bg-white/5 text-white/40 hover:bg-white/10' : 'bg-gray-100 text-gray-500 hover:bg-gray-200')
                    }`}
                  >
                    {expandTimeline ? <FontAwesomeIcon icon={faCompress} className="h-3 w-3" /> : <FontAwesomeIcon icon={faExpand} className="h-3 w-3" />}
                    {expandTimeline ? 'Collapse' : 'Expand'}
                  </button>
                </div>
                <div className={`flex-1 overflow-y-auto rounded-b-xl border border-t-0 p-4 ${theme.card}`}>
                {Array.from(commitsByDate.entries()).map(([dateLabel, dateCommits]) => (
                  <div key={dateLabel} className="mb-6 last:mb-0">
                    {/* Date header */}
                    <div className="flex items-center gap-3 mb-3 sticky top-0 z-10">
                      <div className={`text-xs font-semibold px-3 py-1 rounded-full ${
                        isDark ? 'bg-white/10 text-white/70' : 'bg-gray-200 text-gray-700'
                      }`}>
                        {dateLabel}
                      </div>
                      <div className={`flex-1 h-px ${isDark ? 'bg-white/10' : 'bg-gray-200'}`} />
                      <span className={`text-[10px] ${theme.textMuted}`}>{dateCommits.length} commits</span>
                    </div>

                    {/* Commits for this date */}
                    <div className="space-y-1">
                      {dateCommits.map(commit => {
                        const color = repoColorMap.get(commit.repoId);
                        return (
                          <div
                            key={`${commit.repoId}-${commit.hash}`}
                            onClick={() => handleCommitClick(commit)}
                            className={`flex items-start gap-3 px-3 py-2 rounded-lg transition-colors cursor-pointer ${
                              selectedCommit?.hash === commit.hash && selectedCommit?.repoId === commit.repoId
                                ? (isDark ? 'bg-indigo-500/10 border border-indigo-500/20' : 'bg-indigo-50 border border-indigo-200')
                                : (isDark ? 'hover:bg-slate-700/30 border border-transparent' : 'hover:bg-gray-50 border border-transparent')
                            }`}
                          >
                            {/* Timeline dot */}
                            <div className="flex flex-col items-center pt-1.5 flex-shrink-0">
                              <div className={`w-2.5 h-2.5 rounded-full ${color?.dot || 'bg-gray-400'}`} />
                            </div>

                            {/* Commit info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-0.5">
                                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${color?.light || ''} ${color?.text || theme.textMuted}`}>
                                  {commit.repoName}
                                </span>
                                <code className={`text-[10px] font-mono ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`}>
                                  {commit.hash.substring(0, 7)}
                                </code>
                                <span className={`text-[10px] ${theme.textMuted}`}>
                                  {getTimeAgo(commit.date)}
                                </span>
                              </div>
                              <p className={`text-sm leading-snug ${theme.text}`} style={{
                                display: '-webkit-box',
                                WebkitLineClamp: 1,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden'
                              }}>
                                {commit.message}
                              </p>
                              <div className={`flex items-center gap-2 mt-0.5 text-[11px] ${theme.textMuted}`}>
                                <span className="font-medium">{commit.author}</span>
                                <span>on {commit.branch || 'unknown'}</span>
                                <span className={isDark ? 'text-emerald-400/70' : 'text-emerald-600'}>+{commit.additions}</span>
                                <span className={isDark ? 'text-rose-400/70' : 'text-rose-600'}>-{commit.deletions}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}

                {/* Load more */}
                {displayedCommits.length > visibleCount && (
                  <button
                    onClick={() => setVisibleCount(prev => prev + 50)}
                    className={`w-full mt-4 flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-medium transition-colors ${
                      isDark ? 'bg-white/5 hover:bg-white/10 text-white/50' : 'bg-gray-100 hover:bg-gray-200 text-gray-500'
                    }`}
                  >
                    <FontAwesomeIcon icon={faChevronDown} className="h-3 w-3" />
                    Load more ({displayedCommits.length - visibleCount} remaining)
                  </button>
                )}
              </div>
              </div>
            )}
          </div>
        </div>

        {/* Diff Viewer Panel — slides in from the right */}
        {selectedCommit && (
          <div className={`fixed right-0 top-0 h-full w-[55%] max-w-3xl z-[60] flex flex-col shadow-2xl border-l transition-transform ${
            isDark ? 'bg-slate-950 border-white/10' : 'bg-white border-gray-200'
          }`}>
            {/* Diff header */}
            <div className={`flex items-center justify-between p-4 border-b flex-shrink-0 ${
              isDark ? 'border-white/10' : 'border-gray-200'
            }`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${repoColorMap.get(selectedCommit.repoId)?.dot || 'bg-gray-400'}`} />
                  <span className={`text-xs font-medium ${repoColorMap.get(selectedCommit.repoId)?.text || theme.textMuted}`}>
                    {selectedCommit.repoName}
                  </span>
                  <code className={`text-xs font-mono ${isDark ? 'text-indigo-400 bg-indigo-500/10' : 'text-indigo-600 bg-indigo-100'} px-1.5 py-0.5 rounded`}>
                    {selectedCommit.hash.substring(0, 7)}
                  </code>
                </div>
                <p className={`text-sm font-medium truncate ${theme.text}`}>
                  {selectedCommit.message}
                </p>
                <div className={`flex items-center gap-3 mt-1 text-xs ${theme.textMuted}`}>
                  <span className="font-medium">{selectedCommit.author}</span>
                  <span>{new Date(selectedCommit.date).toLocaleString()}</span>
                  <span className={isDark ? 'text-emerald-400' : 'text-emerald-600'}>+{selectedCommit.additions}</span>
                  <span className={isDark ? 'text-rose-400' : 'text-rose-600'}>-{selectedCommit.deletions}</span>
                </div>
              </div>
              <button
                onClick={() => { setSelectedCommit(null); setDiff(''); }}
                className={`p-2 rounded-lg ml-3 flex-shrink-0 transition-colors ${
                  isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'
                }`}
              >
                <FontAwesomeIcon icon={faXmark} className={`h-5 w-5 ${isDark ? 'text-white/60' : 'text-gray-500'}`} />
              </button>
            </div>

            {/* Diff content — collapsible per-file */}
            <div className="flex-1 overflow-y-auto overflow-x-auto p-0">
              {diffLoading ? (
                <div className="flex items-center justify-center h-full gap-2">
                  <FontAwesomeIcon icon={faSpinner} className={`h-5 w-5 animate-spin ${theme.textMuted}`} />
                  <span className={`text-sm ${theme.textMuted}`}>Loading diff...</span>
                </div>
              ) : parsedDiffFiles.length > 0 ? (
                <div className="p-2 space-y-1">
                  {/* File summary bar */}
                  <div className={`flex items-center justify-between px-3 py-2 rounded-lg mb-2 ${
                    isDark ? 'bg-slate-800/30' : 'bg-gray-50'
                  }`}>
                    <span className={`text-xs font-medium ${theme.textMuted}`}>
                      {parsedDiffFiles.length} file{parsedDiffFiles.length !== 1 ? 's' : ''} changed
                      <span className={`ml-2 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                        +{parsedDiffFiles.reduce((s, f) => s + f.additions, 0)}
                      </span>
                      <span className={`ml-1 ${isDark ? 'text-rose-400' : 'text-rose-600'}`}>
                        -{parsedDiffFiles.reduce((s, f) => s + f.deletions, 0)}
                      </span>
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={expandAllDiffFiles}
                        className={`text-xs px-2 py-0.5 rounded transition-colors ${
                          isDark ? 'hover:bg-white/10 text-white/50' : 'hover:bg-gray-200 text-gray-500'
                        }`}
                      >
                        Expand all
                      </button>
                      <button
                        onClick={collapseAllDiffFiles}
                        className={`text-xs px-2 py-0.5 rounded transition-colors ${
                          isDark ? 'hover:bg-white/10 text-white/50' : 'hover:bg-gray-200 text-gray-500'
                        }`}
                      >
                        Collapse all
                      </button>
                    </div>
                  </div>

                  {parsedDiffFiles.map((file, fileIdx) => {
                    const isExpanded = expandedDiffFiles.has(fileIdx);
                    const totalChanges = file.additions + file.deletions;
                    const addPct = totalChanges > 0 ? Math.round((file.additions / totalChanges) * 100) : 0;
                    return (
                      <div key={fileIdx} className={`rounded-lg border overflow-hidden ${
                        isDark ? 'border-white/10' : 'border-gray-200'
                      }`}>
                        {/* File header — always visible */}
                        <button
                          onClick={() => toggleDiffFile(fileIdx)}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-left transition-colors ${
                            isDark
                              ? 'bg-slate-800/30 hover:bg-slate-700/40'
                              : 'bg-gray-50 hover:bg-gray-100'
                          }`}
                        >
                          {isExpanded
                            ? <FontAwesomeIcon icon={faChevronDown} className={`h-3.5 w-3.5 flex-shrink-0 ${theme.textMuted}`} />
                            : <FontAwesomeIcon icon={faChevronRight} className={`h-3.5 w-3.5 flex-shrink-0 ${theme.textMuted}`} />
                          }
                          <FontAwesomeIcon icon={faFile} className={`h-3.5 w-3.5 flex-shrink-0 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                          <span className={`text-xs font-mono truncate flex-1 ${theme.text}`}>
                            {file.filename}
                          </span>
                          {/* +/- badge bar (GitHub-style) */}
                          <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                            <span className={`text-xs font-mono font-medium ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                              +{file.additions}
                            </span>
                            <span className={`text-xs font-mono font-medium ${isDark ? 'text-rose-400' : 'text-rose-600'}`}>
                              -{file.deletions}
                            </span>
                            {/* Mini change bar */}
                            <div className="flex h-2 w-12 rounded-full overflow-hidden bg-gray-700/20 ml-1">
                              <div
                                className={isDark ? 'bg-emerald-500' : 'bg-emerald-500'}
                                style={{ width: `${addPct}%` }}
                              />
                              <div
                                className={isDark ? 'bg-rose-500' : 'bg-rose-500'}
                                style={{ width: `${100 - addPct}%` }}
                              />
                            </div>
                          </div>
                        </button>

                        {/* File diff lines — collapsible */}
                        {isExpanded && (
                          <div className="overflow-x-auto">
                            {file.lines.map((line, lineIdx) => {
                              let lineClass = `font-mono text-xs whitespace-pre px-4 py-0.5 ${isDark ? 'text-white/80' : 'text-gray-800'}`;
                              if (line.startsWith('+') && !line.startsWith('+++')) {
                                lineClass = `font-mono text-xs whitespace-pre px-4 py-0.5 ${
                                  isDark ? 'bg-green-500/10 text-green-300' : 'bg-green-100 text-green-800'
                                }`;
                              } else if (line.startsWith('-') && !line.startsWith('---')) {
                                lineClass = `font-mono text-xs whitespace-pre px-4 py-0.5 ${
                                  isDark ? 'bg-red-500/10 text-red-300' : 'bg-red-100 text-red-800'
                                }`;
                              } else if (line.startsWith('@@')) {
                                lineClass = `font-mono text-xs whitespace-pre px-4 py-0.5 ${
                                  isDark ? 'bg-blue-500/10 text-blue-300' : 'bg-blue-100 text-blue-800'
                                }`;
                              } else if (line.startsWith('diff --git') || line.startsWith('index ') || line.startsWith('---') || line.startsWith('+++')) {
                                lineClass = `font-mono text-xs whitespace-pre px-4 py-0.5 font-semibold ${
                                  isDark ? 'text-white/50 bg-slate-800/20' : 'text-gray-500 bg-gray-50'
                                }`;
                              }
                              return <div key={lineIdx} className={lineClass}>{line || ' '}</div>;
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="p-4">
                  <pre className={`font-mono text-xs whitespace-pre-wrap ${theme.textMuted}`}>{diff}</pre>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <footer className="mt-3 text-center flex-shrink-0">
          <span className={`text-xs ${theme.textSubtle}`}>
            <kbd className={`px-1.5 py-0.5 rounded font-mono ${isDark ? 'bg-white/5 text-white/40' : 'bg-gray-200 text-gray-500'}`}>ESC</kbd> exit
            <span className="mx-2">&middot;</span>
            <kbd className={`px-1.5 py-0.5 rounded font-mono ${isDark ? 'bg-white/5 text-white/40' : 'bg-gray-200 text-gray-500'}`}>T</kbd> theme
            <span className="mx-2">&middot;</span>
            <kbd className={`px-1.5 py-0.5 rounded font-mono ${isDark ? 'bg-white/5 text-white/40' : 'bg-gray-200 text-gray-500'}`}>F</kbd> filters
            <span className="mx-2">&middot;</span>
            <kbd className={`px-1.5 py-0.5 rounded font-mono ${isDark ? 'bg-white/5 text-white/40' : 'bg-gray-200 text-gray-500'}`}>C</kbd> charts
            <span className="mx-2">&middot;</span>
            <kbd className={`px-1.5 py-0.5 rounded font-mono ${isDark ? 'bg-white/5 text-white/40' : 'bg-gray-200 text-gray-500'}`}>M</kbd> merges
            <span className="mx-2">&middot;</span>
            <kbd className={`px-1.5 py-0.5 rounded font-mono ${isDark ? 'bg-white/5 text-white/40' : 'bg-gray-200 text-gray-500'}`}>S</kbd> selector
          </span>
        </footer>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, color, isDark }: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  color: 'blue' | 'indigo' | 'violet' | 'emerald' | 'rose';
  isDark: boolean;
}) {
  const darkColors = {
    blue: 'bg-blue-500/10 border-blue-500/20 text-blue-400',
    indigo: 'bg-indigo-500/10 border-indigo-500/20 text-indigo-400',
    violet: 'bg-violet-500/10 border-violet-500/20 text-violet-400',
    emerald: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400',
    rose: 'bg-rose-500/10 border-rose-500/20 text-rose-400',
  };

  const lightColors = {
    blue: 'bg-blue-50 border-blue-200 text-blue-600',
    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-600',
    violet: 'bg-violet-50 border-violet-200 text-violet-600',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-600',
    rose: 'bg-rose-50 border-rose-200 text-rose-600',
  };

  const colors = isDark ? darkColors : lightColors;

  return (
    <div className={`${colors[color]} border rounded-xl p-3`}>
      <div className="flex items-center gap-3">
        <div className="opacity-60">{icon}</div>
        <div>
          <div className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{value}</div>
          <div className={`text-[10px] ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{label}</div>
        </div>
      </div>
    </div>
  );
}

function formatNumber(num: number): string {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toLocaleString();
}

function getTimeAgo(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}
